import styled, { css } from '@xstyled/styled-components';
import React, { FC } from 'react';

interface ArrowProps {
  placement?: string | undefined;
}

const Arrow = styled.div<ArrowProps>`
  ::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    position: absolute;
    border: 10px solid transparent;
    border-top: 10px solid ${p => p.theme.colors.yellowLight};
    margin-top: 14px;
    z-index: 1;
    right: ${p => (p.placement == 'bottom-right' ? '20px' : null)};
    left: ${p => (p.placement === 'left' ? '-17px' : null)};
    top: ${p => (p.placement === 'left' ? '0' : null)};
    transform: ${p => (p.placement === 'left' ? 'rotate(90deg)' : null)};
    ${p =>
      p.placement === 'top-right'
        ? css`
            top: -10px;
            right: 20px;
            border-top: none;
            border-bottom: 10px solid ${p => p.theme.colors.yellowLight};
            margin-top: 0;
          `
        : null}
    ${p =>
      p.placement === 'bottom-center-up'
        ? css`
            top: -10px;
            right: 20px;
            border-top: none;
            border-bottom: 10px solid ${p => p.theme.colors.yellowLight};
            margin-top: 0;
          `
        : null}
    ${p =>
      p.placement === 'bottom-left' || p.placement === 'bottom-right'
        ? css`
            top: auto;
            bottom: -10px;
            margin-top: 0;
            border: none;
            border-left: 10px solid transparent;
            border-right: 10px solid transparent;
            border-top: 10px solid ${p.theme.colors.yellowLight};
            border-bottom: none;
            transform: none;
            left: ${p.placement === 'bottom-left' ? '20px' : 'auto'};
            right: ${p.placement === 'bottom-right' ? '20px' : 'auto'};
          `
        : null}
  }
`;

interface TooltipProps {
  placement?: string | undefined;
  right?: string | undefined;
  bottom?: string | undefined;
  left?: string | undefined;
  top?: string | undefined;
}

const TooltipBox = styled.div<TooltipProps>`
  position: absolute;
  color: black;
  background-color: yellowLight;
  min-width: 175px;
  padding: 16px;
  border-radius: 8px;
  border: 2px solid;
  font-family: default;
  font-size: 16px;
  text-align: left;

  ::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    position: absolute;
    border: 10px solid transparent;
    border-top: 10px solid ${p => p.theme.colors.black};
    bottom: auto;
    margin-top: 17px;
    right: ${p => (p.placement == 'bottom-right' ? '20px' : undefined)};
    left: ${p => (p.placement === 'left' ? '-20px' : null)};
    top: ${p => (p.placement === 'left' ? '-3px' : null)};
    transform: ${p => (p.placement === 'left' ? 'rotate(90deg)' : null)};
    ${p =>
      p.placement === 'top-right'
        ? css`
            top: -39px;
            right: 20px;
            transform: rotate(180deg);
          `
        : null}
    ${p =>
      p.placement === 'bottom-center-up'
        ? css`
            top: -13px;
            right: 20px;
            border-top: none;
            border-bottom: 10px solid ${p => p.theme.colors.black};
            margin-top: 0;
          `
        : null}
    ${p =>
      p.placement === 'bottom-left' || p.placement === 'bottom-right'
        ? css`
            top: auto;
            bottom: -12px;
            margin-top: 0;
            border: none;
            inset: auto auto -13px 17px;
            border-left: 14px solid transparent;
            border-right: 11px solid transparent;
            border-top: 13px solid ${p.theme.colors.black};
            border-bottom: none;
            transform: none;
            left: ${p.placement === 'bottom-left' ? '16px' : 'auto'};
            right: ${p.placement === 'bottom-right' ? '16px' : 'auto'};
          `
        : null}
  }
  right: ${p => p.right};
  bottom: ${p => p.bottom};
  left: ${p => p.left};
  top: ${p => p.top};

  ${p =>
    p.placement === 'none'
      ? css`
          ::after {
            display: none;
          }
        `
      : null}
`;

interface Props {
  left?: string | undefined;
  top?: string | undefined;
  right?: string | undefined;
  bottom?: string | undefined;
  placement?: string | undefined;
  className?: string | undefined;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const Tooltip: FC<Props> = ({ children, ...props }) => {
  return (
    <TooltipBox {...props}>
      {children}
      {props.placement !== 'none' && (
        <Arrow className="tooltip-arrow" placement={props.placement} />
      )}
    </TooltipBox>
  );
};

Tooltip.defaultProps = {
  placement: 'left-bottom',
  right: undefined,
  bottom: undefined
};
